export function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US')
}

export function round2(value: number): number {
  return Math.round(value * 100) / 100
}

// Formats a currency value for CSV export as a fixed 2-decimal string. Accepts
// `unknown` because DataTable's export formatters receive raw cell values;
// non-numbers (e.g. a blank/absent cell) export as an empty string.
export function currencyExport(value: unknown): string {
  return typeof value === 'number' ? value.toFixed(2) : ''
}

/** An API timestamp: epoch milliseconds, a legacy ISO string, or absent. */
type EpochValue = number | string | null | undefined

/**
 * Resolves an API timestamp to a Date, or null when there is no real moment to
 * show.
 *
 * api-core marshals `CassandraTime` as epoch milliseconds (a bare JSON number)
 * and as `null` for a zero time, so an unguarded `new Date(value)` renders the
 * Unix epoch as a real date. Strings are accepted for the legacy ISO shape.
 *
 * A literal `0` counts as absent. v4-era payloads use epoch zero as a "no
 * timestamp" sentinel where v5 sends null, and no record in a POS system is
 * genuinely stamped 1 January 1970 — so rendering "Dec 31, 1969" is always
 * wrong, whichever era fed it.
 */
function toDisplayDate(value: EpochValue): Date | null {
  if (value === null || value === undefined || value === '' || value === 0) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Timestamp for detail pages: short month, no seconds.
 *
 * The locale is pinned for the same reason `formatCurrency` pins it — output
 * should not vary by browser.
 */
export function formatEpochDateTime(value: EpochValue, placeholder = '—'): string {
  const date = toDisplayDate(value)
  if (!date) return placeholder
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Timestamp for dense tables: numeric date, and seconds.
 *
 * Seconds are load-bearing here rather than noise — ledger rows for one
 * customer routinely land in the same minute, so dropping them would render two
 * distinct entries with an identical timestamp.
 */
export function formatEpochTimestamp(value: EpochValue, placeholder = ''): string {
  const date = toDisplayDate(value)
  if (!date) return placeholder
  return date.toLocaleString('en-US')
}

export const RIGHT_ALIGN_META = { className: 'text-right' }

export const FOOTER_CELL = 'px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white'
